import Head from 'next/head'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { useRouter } from 'next/router'
import { localeNames, localePath, siteLocales, toLocale } from '@/i18n/config'
import { common } from '@/i18n/common'
import { siteConfig } from '@/constants/site'

const cleanPath = (value: string): string => {
    const [path] = value.split(/[?#]/)
    if (!path || path === '') return '/'
    return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path
}

export const absoluteUrl = (path: string): string => `${siteConfig.url}${path === '/' ? '' : path}`

export const Seo: FC<SeoProps> = ({
    title,
    description,
    ogImage,
    path,
    noindex = false,
    type = 'website',
    jsonLd = [],
    breadcrumbs,
    alternates = true
}): ReactNode => {
    const router = useRouter()
    const locale = toLocale(router.locale)
    const pagePath = cleanPath(path ?? router.asPath ?? '/')
    const pageTitle = title ? `${title} - ${siteConfig.name}` : siteConfig.name
    const pageDescription = description ?? common[locale].description
    const canonical = absoluteUrl(localePath(locale, pagePath))
    const image = ogImage ?? absoluteUrl(`/api/og?title=${encodeURIComponent(title ?? common[locale].tagline)}&locale=${locale}`)

    const breadcrumbLd = breadcrumbs && breadcrumbs.length > 0
        ? [{
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
                { name: common[locale].breadcrumbHome, path: '/' },
                ...breadcrumbs
            ].map((item, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: item.name,
                item: absoluteUrl(localePath(locale, item.path))
            }))
        }]
        : []

    const structured = [...jsonLd, ...breadcrumbLd]

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta name='description' content={pageDescription} />
            {noindex && <meta name='robots' content='noindex, nofollow' />}
            {!noindex && <link rel='canonical' href={canonical} />}

            {!noindex && alternates && siteLocales.map(item => (
                <link
                    key={item}
                    rel='alternate'
                    hrefLang={localeNames[item].hreflang}
                    href={absoluteUrl(localePath(item, pagePath))}
                />
            ))}

            {!noindex && alternates && <link rel='alternate' hrefLang='x-default' href={absoluteUrl(pagePath)} />}

            <meta property='og:site_name' content={siteConfig.name} />
            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={pageDescription} />
            <meta property='og:type' content={type} />
            <meta property='og:url' content={canonical} />
            <meta property='og:locale' content={locale === 'sq' ? 'sq_AL' : 'en_GB'} />
            <meta property='og:image' content={image} />
            <meta property='og:image:width' content='1200' />
            <meta property='og:image:height' content='630' />
            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={pageDescription} />
            <meta name='twitter:image' content={image} />

            {structured.map((item, index) => (
                <script
                    key={index}
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(item).replace(/</g, '\\u003c') }}
                />
            ))}
        </Head>
    )
}
